import React from "react";
import { Link } from "react-router-dom";
import { MURALS } from "../data/site";
import { ArrowRight } from "lucide-react";

export default function Murals() {
  return (
    <div data-testid="murals-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">Public & Commissioned Walls</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Murals.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] text-lg leading-relaxed">
          {MURALS.intro}
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {MURALS.works.length === 0 ? (
          <p className="text-[hsl(var(--ink-soft))]">
            Mural portfolio coming soon. For commission inquiries,{" "}
            <Link to="/contact" className="link-underline text-foreground">
              contact the studio
            </Link>
            .
          </p>
        ) : (
          <div className="space-y-16 md:space-y-24">
            {MURALS.works.map((m) => (
              <figure key={m.id} data-testid={`mural-${m.id}`}>
                <div className="aspect-[16/9] overflow-hidden bg-[hsl(var(--surface))]">
                  <img
                    src={m.image}
                    alt={m.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <figcaption className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3 border-b border-border pb-5">
                  <div>
                    <h2 className="serif text-3xl md:text-4xl tracking-tight">
                      {m.title}
                    </h2>
                    <p className="label mt-1">
                      {m.location} · {m.year}
                    </p>
                    {m.commission && (
                      <p className="serif italic text-base text-[hsl(var(--ink-soft))] mt-2">
                        {m.commission}
                      </p>
                    )}
                  </div>
                </figcaption>
                {m.story && (
                  <p className="mt-6 max-w-3xl text-[hsl(var(--ink-soft))] leading-relaxed" data-testid={`mural-story-${m.id}`}>
                    {m.story}
                  </p>
                )}
                {m.press && (
                  <blockquote className="mt-8 max-w-3xl border-l-2 border-[hsl(var(--accent))] pl-6" data-testid={`mural-press-${m.id}`}>
                    <p className="serif italic text-xl md:text-2xl leading-snug">“{m.press.quote}”</p>
                    <footer className="mt-3 label text-[hsl(var(--ink-soft))]">
                      {m.press.attribution} ·{" "}
                      <a href={m.press.link} target="_blank" rel="noreferrer" className="link-underline text-foreground" data-testid={`mural-press-link-${m.id}`}>
                        {m.press.outlet} →
                      </a>
                    </footer>
                  </blockquote>
                )}
                {m.gallery?.length > 0 && (
                  <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid={`mural-gallery-${m.id}`}>
                    {m.gallery.map((g) => (
                      <figure key={g.src}>
                        <div className="aspect-[3/2] overflow-hidden bg-[hsl(var(--surface))]">
                          <img src={g.src} alt={g.caption} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <figcaption className="mt-2 text-xs text-[hsl(var(--ink-soft))] leading-snug">{g.caption}</figcaption>
                      </figure>
                    ))}
                  </div>
                )}
              </figure>
            ))}
          </div>
        )}
      </div>

      <section className="border-t border-border bg-[hsl(var(--surface))]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <p className="label mb-4">Commission a Mural</p>
          <h2 className="serif text-4xl md:text-5xl tracking-tight font-light">
            Bring a wall to life.
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[hsl(var(--ink-soft))]">
            Mural commissions accepted for residences, hospitality lobbies,
            corporate spaces, and community projects across the Front Range
            and beyond.
          </p>
          <Link
            to="/contact?subject=Mural%20Quote%20Request&message=Hello%20%E2%80%94%20I%27d%20like%20a%20quote%20for%20a%20mural.%20Location%3A%20%20%7C%20Approximate%20wall%20size%3A%20%20%7C%20Indoor%2Foutdoor%3A%20%20%7C%20Timeline%3A%20"
            className="mt-8 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90"
            data-testid="murals-commission-cta"
          >
            Request a mural quote <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
